import { Component, OnInit, inject, signal } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { SplashScreen } from '@capacitor/splash-screen';
import { SaleSplashComponent } from './shared/sale-splash/sale-splash.component';
import { CityPickerComponent } from './shared/city-picker/city-picker.component';
import { PaymentNotificationService } from './core/services/payment-notification.service';
import { TokenService } from './core/services/token.service';
import { CityService } from './core/services/city.service';
import { SocketService } from './core/services/socket.service';
import { PermissionService } from './core/services/permission.service';
import { TrackingService } from './core/services/tracking.service';
import { EmergencyService } from './core/services/emergency.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [IonApp, IonRouterOutlet, SaleSplashComponent, CityPickerComponent],
})
export class AppComponent implements OnInit {
  readonly showSplash = signal(true);

  /**
   * Resolves once the splash overlay has left the screen.
   *
   * The splash is position:fixed at z-index 99999 and lives outside <ion-app>,
   * so it paints over Ionic's overlay layer: anything presented while it is up
   * is invisible and untappable. It always mounts and always leaves (it has its
   * own ceiling), so awaiting this cannot hang.
   */
  private resolveSplashGone!: () => void;
  private readonly splashGone = new Promise<void>(resolve => { this.resolveSplashGone = resolve; });

  onSplashFinished(): void {
    this.showSplash.set(false);
    this.resolveSplashGone();
  }

  private readonly tokenService         = inject(TokenService);
  private readonly paymentNotifications = inject(PaymentNotificationService);
  private readonly permissionService    = inject(PermissionService);
  private readonly trackingService      = inject(TrackingService);
  private readonly emergencyService     = inject(EmergencyService);
  private readonly socketService        = inject(SocketService);
  readonly cityService                  = inject(CityService);

  ngOnInit() {
    // Hand off from the native splash to the web one, and only once the web
    // one has actually painted. Calling hide() on the first line of ngOnInit
    // took the native splash down before Angular's first paint, which showed
    // as about a second of white webview between the two. One rAF runs before
    // the paint, so the second one is the first moment <sale-splash> is really
    // on screen. The timer is for a launch straight into the background, where
    // rAF never fires. hide() is idempotent, so running twice is harmless.
    const hideNativeSplash = () => { void SplashScreen.hide({ fadeOutDuration: 0 }).catch(() => {}); };
    requestAnimationFrame(() => requestAnimationFrame(hideNativeSplash));
    setTimeout(hideNativeSplash, 2_000);

    // Deliberately not awaited. None of this decides what the driver sees
    // first — startupRedirectGuard does that from the token, before the first
    // navigation — so holding the splash over all of it only ever made the app
    // feel slow. It now runs behind whichever screen the guard picked.
    void this.startBackgroundInit();
  }

  /**
   * Best-effort startup. Order still matters inside it: permissions own the
   * native dialog (with disclosure) and must settle before anything reads GPS,
   * and the socket can only pick a silo once the city is resolved.
   */
  private async startBackgroundInit(): Promise<void> {
    // Nothing in here starts while the splash is up, and the splash does not
    // wait for any of it. The two are fully decoupled, in that order.
    //
    // The disclaimer is an ion-alert, and the splash is position:fixed at
    // z-index 99999 outside <ion-app>, so it paints over Ionic's overlay layer.
    // Presented underneath it the alert is invisible and untappable, and the
    // permission chain then waits forever on a tap the driver has no way to
    // make — which is what left the app sitting on the loading bar.
    await this.splashGone;

    try {
      // The guard already did this; init() is idempotent and this guarantees
      // the token is in memory even if the guard has not resolved yet.
      await this.tokenService.init();

      // Permissions first: disclaimer → native popup.
      await this.permissionService.requestOnboardingPermissions();

      // City AFTER permissions, so GPS is only read once it has been granted.
      // CityService warm-starts from the last resolved city, so requests made
      // before this finishes already go to the right silo on any launch but
      // the first.
      await this.cityService.resolve();

      // Tracking AFTER permissions (trackSessionStart uses GPS).
      this.trackingService.init();

      const token = this.tokenService.getToken();
      if (token) {
        this.socketService.connectToCity();
        this.socketService.authenticate(token);
      }
    } catch (err) {
      console.error('[AppComponent] background startup failed, continuing anyway', err);
    }

    void this.paymentNotifications.init();

    // Keep the driver/tenant emergency rooms joined for the whole session.
    // Registered here (not in active-trip) so a reconnect while the driver is
    // on any screen still restores membership in tenant-trips-<tenantId>.
    this.emergencyService.initRoomRejoin();
  }
}
